import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import {
  PartnerServiceType,
  PlanningCandidateKind,
  PlanningCandidateModel,
  PlanningCandidatesService,
  PlanningSuggestionModel,
  PlanningSuggestionsService,
  isBindable,
  isBound,
} from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, of, switchMap, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { SpinnerComponent } from '../components/common/spinner.component';
import { PlaceChoice, PlacePickerComponent } from './place-picker.component';

/**
 * One offered visit or order with the answer already worked out, so the row itself decides nothing.
 * Either a record of ours or a visit held only in the partner's system — one list, one choice.
 */
interface CandidateRow {
  key: string;
  candidate?: PlanningCandidateModel;
  suggestion?: PlanningSuggestionModel;
  dateStart: string;
  dateEnd?: string;
  partnerName?: string;
  locationName?: string;
  doctorName?: string;
  serviceName?: string;
  orderNumber?: string;
  price?: number;
  /** Why this one cannot be taken, or empty where it can. */
  refusalKey: string;
  selected: boolean;
  className: string;
}

const ROW_BASE =
  'flex w-full flex-col gap-1 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none';
const ROW_OPEN = `${ROW_BASE} border-neutral-200 bg-neutral-0 hover:bg-neutral-50`;
const ROW_SELECTED = `${ROW_BASE} border-primary-300 bg-primary-50`;
const ROW_REFUSED = `${ROW_BASE} cursor-not-allowed border-neutral-100 bg-neutral-50`;

function refusalKeyOf(candidate: PlanningCandidateModel): string {
  if (isBound(candidate)) {
    return 'guaranteeV2.message.candidateAlreadyBound';
  }

  return isBindable(candidate) ? '' : 'guaranteeV2.message.candidateNotOpen';
}

/** Newest first by the instant itself: our records and the partner's visits spell their dates differently. */
function byDateDesc(a: CandidateRow, b: CandidateRow): number {
  return (Date.parse(b.dateStart) || 0) - (Date.parse(a.dateStart) || 0);
}

/** A partner's visit has no id of ours yet, so it is told apart by what the partner finds it by. */
function suggestionKeyOf(suggestion: PlanningSuggestionModel): string {
  return ['partner', suggestion.partnerId, suggestion.locationId, suggestion.doctorId, suggestion.startDate, suggestion.endDate].join('|');
}

/**
 * The bookings the member already holds, to deliver these lines by one of them.
 *
 * Nothing is listed until a partner is named. Then the list is that partner's: our records with it
 * and, for a visit, the visits booked directly in the partner's own system that we hold no record of yet — offered in the same list, because to the manager both are
 * the same thing: the visit the member already has. A branch is optional; a partner that only
 * answers branch by branch stays silent until one is named.
 *
 * Everything still worth offering is shown, including what cannot be taken: a visit already past or
 * an order already collected is the reason the list looks short, and hiding it only makes the flow
 * look broken. The reason stands in the row as text rather than behind a hover — a row that refuses
 * to be clicked refuses to be hovered as well.
 */
@Component({
  selector: 'guarantees-candidate-picker',
  standalone: true,
  imports: [DatePipe, MatIcon, TranslatePipe, CalloutComponent, SpinnerComponent, PlacePickerComponent],
  // The dialog body is the one scroll; the host takes a block of its own so the list sits in it whole.
  host: { class: 'block' },
  templateUrl: './candidate-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidatePickerComponent implements OnInit {
  memberId = input.required<string>();
  kind = input.required<PlanningCandidateKind>();
  /** Which partners the selector offers. */
  service = input.required<PartnerServiceType>();
  /** Whether the partner's own system is asked as well — only a visit is held there in a form we can take over. */
  withPartnerVisits = input<boolean>(false);
  /** What was taken before, so stepping back and forward again shows the answer rather than blanks. */
  selected = input<PlanningCandidateModel | undefined>(undefined);
  selectedSuggestion = input<PlanningSuggestionModel | undefined>(undefined);
  /** The partner and branch asked before, kept by the form so the step does not ask twice. */
  initialPlace = input<PlaceChoice | undefined>(undefined);

  chosen = output<PlanningCandidateModel>();
  suggestionChosen = output<PlanningSuggestionModel>();
  placeChanged = output<PlaceChoice | undefined>();

  private readonly candidates = inject(PlanningCandidatesService);
  private readonly suggestions = inject(PlanningSuggestionsService);

  /** Bumped to ask again after a read that failed — nothing about the question has changed. */
  private readonly attempt = signal(0);

  protected readonly place = signal<PlaceChoice | undefined>(undefined);

  protected readonly loading = signal(false);
  protected readonly failed = signal(false);
  protected readonly loadingSuggestions = signal(false);
  protected readonly suggestionsFailed = signal(false);

  private readonly offered = signal<PlanningCandidateModel[]>([]);
  private readonly suggested = signal<PlanningSuggestionModel[]>([]);

  protected readonly rows = computed<CandidateRow[]>(() => {
    const selectedId = this.selected()?.id;
    const selectedSuggestion = this.selectedSuggestion();
    const selectedSuggestionKey = selectedSuggestion ? suggestionKeyOf(selectedSuggestion) : undefined;
    const partnerId = this.place()?.partnerId;
    const partnerName = this.place()?.partnerName;

    const ours = this.offered()
      .filter(candidate => !partnerId || candidate.partnerId === partnerId)
      .map<CandidateRow>(candidate => {
        const refusalKey = refusalKeyOf(candidate);
        const selected = candidate.id === selectedId;

        return {
          key: `record|${candidate.id}`,
          candidate,
          dateStart: candidate.dateStart,
          dateEnd: candidate.dateEnd,
          partnerName: candidate.partnerName,
          locationName: candidate.locationName || candidate.locationAddress,
          doctorName: candidate.doctorName,
          serviceName: candidate.serviceName,
          orderNumber: candidate.orderNumber,
          price: candidate.price,
          refusalKey,
          selected,
          className: refusalKey ? ROW_REFUSED : selected ? ROW_SELECTED : ROW_OPEN,
        };
      });

    const theirs = this.suggested().map<CandidateRow>(suggestion => {
      const key = suggestionKeyOf(suggestion);
      const selected = key === selectedSuggestionKey;

      return {
        key,
        suggestion,
        dateStart: suggestion.startDate,
        dateEnd: suggestion.endDate,
        partnerName,
        locationName: suggestion.locationName,
        doctorName: suggestion.doctorName,
        serviceName: suggestion.description,
        refusalKey: '',
        selected,
        className: selected ? ROW_SELECTED : ROW_OPEN,
      };
    });

    return [...ours, ...theirs].sort(byDateDesc);
  });

  /** Nothing is asked until a partner is named: the list is always one partner's. */
  protected readonly asked = computed(() => !!this.place()?.partnerId);

  protected readonly busy = computed(() => this.loading() || this.loadingSuggestions());

  protected readonly empty = computed(() => this.asked() && !this.busy() && !this.failed() && this.rows().length === 0);

  /** What the list is for: how many of these could actually be taken, not how many exist. */
  protected readonly bindableCount = computed(() => this.rows().filter(row => !row.refusalKey).length);

  constructor() {
    combineLatest([toObservable(this.memberId), toObservable(this.kind), toObservable(this.asked), toObservable(this.attempt)])
      .pipe(
        tap(() => {
          this.failed.set(false);
          this.offered.set([]);
        }),
        switchMap(([memberId, kind, asked]) => {
          if (!asked) {
            this.loading.set(false);

            return of<PlanningCandidateModel[]>([]);
          }

          this.loading.set(true);

          return this.candidates.search(memberId, kind).pipe(
            catchError(() => {
              this.failed.set(true);

              return of<PlanningCandidateModel[]>([]);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(offered => {
        this.offered.set(offered);
        this.loading.set(false);
      });

    combineLatest([toObservable(this.memberId), toObservable(this.withPartnerVisits), toObservable(this.place), toObservable(this.attempt)])
      .pipe(
        tap(() => {
          this.suggestionsFailed.set(false);
          this.suggested.set([]);
        }),
        switchMap(([memberId, withPartnerVisits, place]) => {
          if (!withPartnerVisits || !place?.partnerId) {
            this.loadingSuggestions.set(false);

            return of<PlanningSuggestionModel[]>([]);
          }

          this.loadingSuggestions.set(true);

          return this.suggestions.search(memberId, place.partnerId, place.locationId).pipe(
            catchError(() => {
              this.suggestionsFailed.set(true);

              return of<PlanningSuggestionModel[]>([]);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(suggested => {
        this.suggested.set(suggested);
        this.loadingSuggestions.set(false);
      });
  }

  /** Read here rather than in the constructor: an input has no value until Angular has bound it. */
  ngOnInit(): void {
    this.place.set(this.initialPlace());
  }

  protected choosePlace(place: PlaceChoice | undefined): void {
    this.place.set(place);
    this.placeChanged.emit(place);
  }

  protected choose(row: CandidateRow): void {
    if (row.refusalKey) {
      return;
    }

    if (row.suggestion) {
      this.suggestionChosen.emit(row.suggestion);
    } else if (row.candidate) {
      this.chosen.emit(row.candidate);
    }
  }

  protected retry(): void {
    this.attempt.update(count => count + 1);
  }
}
